import { useState, useCallback } from 'react';

interface PopupState {
  isOpen: boolean;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  showCancel?: boolean;
}

export const usePopup = () => {
  const [popupState, setPopupState] = useState<PopupState>({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  const showPopup = useCallback((config: Omit<PopupState, 'isOpen'>) => {
    setPopupState({
      ...config,
      isOpen: true
    });
  }, []);

  const hidePopup = useCallback(() => {
    setPopupState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const showSuccess = useCallback((title: string, message: string, confirmText?: string) => {
    showPopup({
      type: 'success',
      title,
      message,
      confirmText
    });
  }, [showPopup]);

  const showError = useCallback((title: string, message: string, confirmText?: string) => {
    showPopup({
      type: 'error',
      title,
      message,
      confirmText
    });
  }, [showPopup]);

  const showWarning = useCallback((title: string, message: string, confirmText?: string) => {
    showPopup({
      type: 'warning',
      title,
      message,
      confirmText
    });
  }, [showPopup]);

  const showInfo = useCallback((title: string, message: string, confirmText?: string) => {
    showPopup({
      type: 'info',
      title,
      message,
      confirmText
    });
  }, [showPopup]);

  const showConfirm = useCallback((
    title: string, 
    message: string, 
    onConfirm: () => void, 
    onCancel?: () => void,
    confirmText?: string,
    cancelText?: string
  ) => {
    showPopup({
      type: 'warning',
      title,
      message,
      onConfirm,
      onCancel,
      showCancel: true,
      confirmText,
      cancelText
    });
  }, [showPopup]);

  return {
    popupState,
    showPopup,
    hidePopup,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm
  };
};

